/**
 * patrimoine-collectivites.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration des pages éditoriales /collectivites/patrimoine/[slug].
 *
 * Décrit les principales typologies de patrimoine public : écoles, mairies,
 * gymnases, piscines, EHPAD, éclairage public, réseaux de chaleur, logement social.
 */

import type { ParcoursStub } from './parcours-particuliers';

export const PATRIMOINE_COLLECTIVITES: ParcoursStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'ecoles',
    iconName: 'home',
    eyebrow: 'Compétence communale',
    title: 'Écoles & groupes scolaires',
    shortLabel: 'Écoles',
    lead: "Rénover vos écoles améliore le confort des élèves et réduit vos consommations. Un programme national cofinance l'ingénierie préalable.",
    badge: 'Compétence communale',
    badgeLabel: "Maître d'ouvrage : commune ou EPCI",
    keyPoints: [
      {
        iconName: 'home',
        label: 'Isolation et menuiseries',
        description: "Isolation, toitures, remplacement des vieux vitrages. Plusieurs primes disponibles.",
      },
      {
        iconName: 'wind',
        label: "Ventilation et qualité de l'air",
        description: "Ventilation adaptée à l'occupation des salles, pour la concentration et la santé des élèves.",
      },
      {
        iconName: 'flame',
        label: 'Chauffage performant',
        description: "Remplacez votre chaudière par une pompe à chaleur, un raccordement réseau de chaleur ou la biomasse. Cumul d'aides possible.",
      },
      {
        iconName: 'users',
        label: "Ingénierie cofinancée",
        description: "Un programme national prend en charge tout ou partie de l'audit, de la programmation et de l'assistance à maîtrise d'ouvrage.",
      },
    ],
    steps: [
      {
        title: 'Diagnostic et programmation',
        description: "Audit énergétique et programmation pluriannuelle. Candidature au programme de cofinancement de l'ingénierie.",
      },
      {
        title: "Maîtrise d'œuvre",
        description: "Sélection d'un maître d'œuvre. Délibération du conseil municipal.",
      },
      {
        title: 'Montage financier',
        description: "Cumul des dotations, des primes disponibles et des prêts bonifiés pour le reste à charge.",
      },
      {
        title: 'Travaux et réception',
        description: "Travaux planifiés pendant les vacances scolaires. Réception et suivi des consommations.",
      },
    ],
    faq: [
      {
        question: "Mon école est-elle concernée par les obligations réglementaires ?",
        answer:
          "Oui si le bâtiment dépasse 1 000 m². Objectif : -40 % d'ici 2030, -50 % d'ici 2040, -60 % d'ici 2050. Déclaration annuelle obligatoire.",
      },
      {
        question: "Que finance le programme d'ingénierie ?",
        answer:
          "Tout ou partie de l'ingénierie préalable : audit, programmation, assistance, étude de faisabilité. Les travaux restent à financer séparément.",
      },
      {
        question: "Comment maintenir l'activité scolaire pendant les travaux ?",
        answer:
          "Trois options : phasage sur les vacances scolaires, relogement temporaire, ou phasage par bâtiment. À anticiper dès la programmation.",
      },
    ],
    ctaLabel: 'Étudier la rénovation de mes écoles',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Primes collectivités', href: '/collectivites/aides/cee-collectivites' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'mairies',
    iconName: 'landmark',
    eyebrow: 'Siège de la collectivité',
    title: 'Mairies & hôtels de ville',
    shortLabel: 'Mairies',
    lead: "Les mairies sont souvent des bâtiments anciens, parfois classés. La rénovation doit concilier économies, respect du patrimoine et continuité du service public.",
    badge: 'Patrimoine sensible',
    badgeLabel: 'Performance énergétique × architecture',
    keyPoints: [
      {
        iconName: 'landmark',
        label: 'Contraintes architecturales',
        description: "Bâtiment ancien, parfois protégé. Solutions compatibles : isolation intérieure, menuiseries performantes.",
      },
      {
        iconName: 'gauge',
        label: 'Pilotage intelligent',
        description: "Si votre puissance dépasse 290 kW, un pilotage centralisé est obligatoire pour optimiser chauffage, éclairage et ventilation.",
      },
      {
        iconName: 'flame',
        label: 'Chauffage performant',
        description: "Remplacement chaudière fioul/gaz, raccordement réseau de chaleur, géothermie. Cumul de financements possible.",
      },
      {
        iconName: 'file-text',
        label: 'Obligation réglementaire',
        description: "Surface supérieure à 1 000 m² : déclaration annuelle obligatoire, -40 % d'ici 2030.",
      },
    ],
    steps: [
      {
        title: 'Audit énergétique et diagnostic patrimonial',
        description: "Audit réglementaire et diagnostic architectural si le bâtiment est protégé.",
      },
      {
        title: 'Programmation et arbitrages',
        description: "Isolation, pilotage, chauffage. Solutions compatibles avec les contraintes architecturales.",
      },
      {
        title: "Marché de maîtrise d'œuvre",
        description: "Consultation des maîtres d'œuvre.",
      },
      {
        title: 'Travaux et continuité de service',
        description: "Phasage permettant le maintien des fonctions essentielles (état civil, salle du conseil).",
      },
    ],
    faq: [
      {
        question: "Ma mairie est-elle concernée par l'obligation de réduction ?",
        answer:
          "Oui si la surface (bureaux, accueil, salle du conseil) atteint 1 000 m². Déclaration annuelle et trajectoire de réduction obligatoires.",
      },
      {
        question: "Le système de pilotage est-il obligatoire ?",
        answer:
          "Uniquement si la puissance de vos équipements dépasse 290 kW. Pour les petites mairies, recommandé mais pas obligatoire.",
      },
      {
        question: "Comment rénover une mairie classée ?",
        answer:
          "Concertation obligatoire avec l'architecte des bâtiments de France. Privilégiez l'isolation intérieure, des menuiseries performantes et l'isolation des combles.",
      },
    ],
    ctaLabel: 'Étudier la rénovation de ma mairie',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Obligation réglementaire, diagnostic', href: '/collectivites/decret-tertiaire' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'gymnases',
    iconName: 'building',
    eyebrow: 'Équipement sportif couvert',
    title: 'Gymnases & complexes sportifs',
    shortLabel: 'Gymnases',
    lead: "Grands volumes et occupation discontinue : chauffage adapté, eau chaude des douches, éclairage LED et ventilation modulée.",
    badge: 'Volume × occupation',
    badgeLabel: 'Chauffage modulé × eau chaude × éclairage',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Chauffage adapté aux grands volumes',
        description: "Les systèmes radiants sont plus efficaces que la convection pour les grands espaces. Pilotage par zones selon l'occupation réelle.",
      },
      {
        iconName: 'droplet',
        label: 'Eau chaude des douches',
        description: "Solaire thermique, pompe à chaleur, récupération de chaleur sur les eaux usées. Cumul d'aides possible.",
      },
      {
        iconName: 'wind',
        label: 'Brassage et ventilation',
        description: "Récupérez la chaleur qui s'accumule en hauteur. Ventilation adaptée aux variations d'occupation.",
      },
      {
        iconName: 'zap',
        label: 'Éclairage performant',
        description: "Remplacez vos vieux luminaires par des LED avec détection de présence et gradation automatique.",
      },
    ],
    steps: [
      {
        title: 'Audit énergétique',
        description: "Mesure des consommations par poste : chauffage, eau chaude, éclairage, ventilation.",
      },
      {
        title: "Plan pluriannuel d'investissement",
        description: "Hiérarchisation des interventions selon le retour sur investissement et les contraintes d'usage.",
      },
      {
        title: 'Marchés et financement',
        description: "Cumul des dotations, des primes disponibles et des prêts bonifiés.",
      },
      {
        title: 'Travaux phasés et suivi',
        description: "Interventions planifiées hors créneaux d'usage. Suivi des consommations après travaux.",
      },
    ],
    faq: [
      {
        question: "Mon gymnase est-il concerné par l'obligation réglementaire ?",
        answer:
          "Oui si la surface (salle, vestiaires, annexes) dépasse 1 000 m². La plupart des complexes sportifs sont concernés. Déclaration annuelle obligatoire.",
      },
      {
        question: "Quel chauffage choisir pour un gymnase ?",
        answer:
          "Les systèmes radiants chauffent les occupants et non l'air en hauteur, plus efficaces dans les grands volumes. Une étude thermique compare les scénarios.",
      },
      {
        question: "Peut-on financer l'eau chaude solaire ?",
        answer:
          "Oui, un dispositif national finance les capteurs solaires thermiques pour l'eau chaude collective.",
      },
    ],
    ctaLabel: 'Étudier mon gymnase',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Aides chaleur renouvelable', href: '/collectivites/aides/fonds-chaleur' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'piscines',
    iconName: 'droplet',
    eyebrow: 'Forte consommation énergétique',
    title: 'Piscines municipales',
    shortLabel: 'Piscines',
    lead: "Les piscines sont les bâtiments publics les plus énergivores. Leviers : récupération de chaleur sur les eaux usées, déshumidification performante, couverture du bassin.",
    badge: 'Forte intensité',
    badgeLabel: 'Eau, air, ventilation, éclairage',
    keyPoints: [
      {
        iconName: 'droplet',
        label: 'Récupérez la chaleur des eaux',
        description: "Des échangeurs captent la chaleur des eaux de vidange avant rejet. Retour sur investissement rapide.",
      },
      {
        iconName: 'wind',
        label: 'Déshumidification performante',
        description: "Remplacez les vieilles centrales d'air par une pompe à chaleur déshumidificatrice. Plusieurs primes disponibles.",
      },
      {
        iconName: 'flame',
        label: 'Couvrez le bassin',
        description: "Une couverture nocturne limite l'évaporation, responsable de jusqu'à 30 % des pertes thermiques. Investissement modeste, gains immédiats.",
      },
      {
        iconName: 'gauge',
        label: 'Pilotage centralisé',
        description: "Un système de gestion coordonne eau, air, ventilation et éclairage selon la fréquentation.",
      },
    ],
    steps: [
      {
        title: 'Audit énergétique',
        description: "Bilan thermique complet : consommations eau, air, ventilation. Repérage des postes les plus coûteux.",
      },
      {
        title: 'Étude de scénarios',
        description: "Comparez rénovation lourde et interventions ciblées. Chiffrage du retour sur investissement par option.",
      },
      {
        title: 'Financement structuré',
        description: "Cumul des dotations, des aides à la chaleur renouvelable, des primes et des prêts bonifiés.",
      },
      {
        title: 'Travaux et remise en service',
        description: "Interventions planifiées sur la fermeture estivale ou annuelle. Tests avant réouverture au public.",
      },
    ],
    faq: [
      {
        question: "Pourquoi une piscine consomme-t-elle autant ?",
        answer:
          "Elle cumule des besoins thermiques exceptionnels : chauffage de l'eau et de l'air, déshumidification, ventilation. Plusieurs centaines de MWh par an sont courants.",
      },
      {
        question: "Quel retour sur investissement ?",
        answer:
          "Actions rapides : récupération de chaleur, couverture nocturne, pilotage. Actions structurantes (pompe à chaleur, solaire) : retour plus long mais éligibles aux aides.",
      },
      {
        question: "Le pilotage centralisé est-il obligatoire ?",
        answer:
          "Oui dans la majorité des cas, la puissance cumulée dépasse le seuil de 290 kW. Les économies d'exploitation amortissent l'investissement.",
      },
    ],
    ctaLabel: 'Diagnostic piscine municipale',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Aides chaleur renouvelable', href: '/collectivites/aides/fonds-chaleur' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'ehpad',
    iconName: 'home',
    eyebrow: 'Établissement médico-social',
    title: 'EHPAD & santé',
    shortLabel: 'EHPAD',
    lead: "Les EHPAD fonctionnent 24h/24 avec un public vulnérable à la chaleur. La rénovation doit concilier économies, confort d'été, qualité de l'air et continuité des soins.",
    badge: 'Occupation 24/24',
    badgeLabel: 'Confort × sécurité sanitaire × continuité',
    keyPoints: [
      {
        iconName: 'home',
        label: "Isolation et confort d'été",
        description: "Isolation par l'extérieur et protections solaires pour limiter les surchauffes estivales. Enjeu critique depuis les récents épisodes caniculaires.",
      },
      {
        iconName: 'droplet',
        label: 'Eau chaude sanitaire sécurisée',
        description: "Production et distribution à température contrôlée. Solutions : pompe à chaleur, solaire thermique, raccordement réseau de chaleur.",
      },
      {
        iconName: 'wind',
        label: "Ventilation et qualité de l'air",
        description: "Ventilation avec récupération de chaleur et filtration adaptée. Surveillance continue dans les chambres et espaces communs.",
      },
      {
        iconName: 'flame',
        label: 'Chauffage par zone',
        description: "Pompe à chaleur, chaudière biomasse ou réseau de chaleur. Pilotage par zone pour adapter la température aux besoins.",
      },
    ],
    steps: [
      {
        title: 'Audit et analyse fonctionnelle',
        description: "Mesure des consommations, analyse de l'occupation. Identification des leviers compatibles avec la continuité des soins.",
      },
      {
        title: 'Programmation avec la direction',
        description: "Planification par zones, en concertation avec la direction et le médecin coordinateur.",
      },
      {
        title: 'Cumul des financements',
        description: "Primes disponibles, dotations, prêts bonifiés, dispositifs régionaux pour les établissements médico-sociaux.",
      },
      {
        title: 'Travaux par phases',
        description: "Interventions par tranches, par étage ou par aile. Préservation du confort des résidents et de la qualité de l'air.",
      },
    ],
    faq: [
      {
        question: "Mon EHPAD est-il concerné par l'obligation réglementaire ?",
        answer:
          "Oui. Les établissements médico-sociaux de plus de 1 000 m² sont soumis à déclaration annuelle et à la trajectoire de -40 % d'ici 2030.",
      },
      {
        question: "Comment gérer le risque sanitaire pendant la rénovation ?",
        answer:
          "Toute intervention sur l'eau chaude doit prévoir un redémarrage avec contrôle bactériologique. Des solutions compatibles existent avec un bon dimensionnement.",
      },
      {
        question: "Qui peut cofinancer les travaux ?",
        answer:
          "Pour les établissements habilités à l'aide sociale, l'agence régionale de santé et le conseil départemental peuvent cofinancer les travaux.",
      },
    ],
    ctaLabel: 'Diagnostic rénovation EHPAD',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Aides publiques disponibles', href: '/collectivites/aides/fonds-vert' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'eclairage-public',
    iconName: 'zap',
    eyebrow: 'Compétence communale ou EPCI',
    title: 'Éclairage public LED & télégestion',
    shortLabel: 'Éclairage public',
    lead: "L'éclairage public pèse lourd dans vos factures. Passer aux LED avec pilotage intelligent réduit les consommations tout en améliorant la qualité de lumière et la sécurité.",
    badge: 'LED + pilotage',
    badgeLabel: 'Gradation × détection × programmation',
    keyPoints: [
      {
        iconName: 'zap',
        label: 'Remplacement LED',
        description: "Remplacez vos lampes sodium et ballons fluorescents par des LED haut rendement. Plusieurs primes disponibles.",
      },
      {
        iconName: 'gauge',
        label: 'Pilotage à distance',
        description: "Contrôlez chaque point lumineux : gradation horaire, détection de présence, programmation fine.",
      },
      {
        iconName: 'sparkles',
        label: 'Biodiversité et ciel nocturne',
        description: "Extinction en cœur de nuit, température de couleur adaptée à la faune locale. Une mesure favorable à la qualité de vie.",
      },
      {
        iconName: 'banknote',
        label: 'Financements multiples',
        description: "Cumul primes, dotations, prêts bonifiés, aides du syndicat d'énergie. Projet souvent rentable même sans subvention.",
      },
    ],
    steps: [
      {
        title: 'Diagnostic du parc',
        description: "Inventaire des points lumineux, technologie en place, état des mâts et armoires.",
      },
      {
        title: 'Étude de rénovation',
        description: "Choix des luminaires, des stratégies de gradation et du système de pilotage adapté à votre territoire.",
      },
      {
        title: 'Marchés et cofinancement',
        description: "Marché de travaux. Cumul de tous les financements disponibles.",
      },
      {
        title: 'Mise en service et exploitation',
        description: "Déploiement progressif par secteur. Formation des équipes, suivi des indicateurs.",
      },
    ],
    faq: [
      {
        question: "Quel retour sur investissement attendre ?",
        answer:
          "Très favorable : la consommation des LED avec gradation est nettement inférieure aux lampes sodium. Le retour est rapide, encore amélioré par le cumul des aides.",
      },
      {
        question: "Quel niveau lumineux respecter ?",
        answer:
          "La norme définit les classes d'éclairage selon le type de voie. Pour les rues résidentielles, des niveaux modérés suffisent et réduisent la puissance installée.",
      },
      {
        question: "Peut-on éteindre l'éclairage en cœur de nuit ?",
        answer:
          "Oui. De nombreuses communes le font entre 23h et 5h. Cette décision relève du maire. C'est favorable à la biodiversité et au sommeil des habitants.",
      },
    ],
    ctaLabel: "Diagnostic éclairage public",
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Primes collectivités', href: '/collectivites/aides/cee-collectivites' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'reseaux-chaleur',
    iconName: 'flame',
    eyebrow: 'Service public local',
    title: 'Réseaux de chaleur urbains',
    shortLabel: 'Réseaux de chaleur',
    lead: "Les réseaux de chaleur alimentés par biomasse, géothermie ou chaleur de récupération sont un levier majeur pour décarboner votre territoire. Des aides nationales substantielles sont disponibles.",
    badge: 'Levier structurant',
    badgeLabel: 'Énergies renouvelables, décarbonation territoriale',
    keyPoints: [
      {
        iconName: 'flame',
        label: "Sources d'énergie prioritaires",
        description: "Bois, géothermie profonde, solaire thermique, chaleur industrielle de récupération, chaleur des eaux usées. Un taux minimal d'énergies renouvelables est exigé pour les aides.",
      },
      {
        iconName: 'landmark',
        label: 'Mode de gestion',
        description: "Régie directe, société d'économie mixte ou délégation de service public. Le choix dépend de la taille du réseau et de vos compétences.",
      },
      {
        iconName: 'percent',
        label: 'TVA réduite pour les abonnés',
        description: "Vos administrés raccordés à un réseau alimenté à plus de 50 % par des énergies renouvelables bénéficient d'une TVA à 5,5 %.",
      },
      {
        iconName: 'file-text',
        label: 'Étude préalable indispensable',
        description: "Étude de faisabilité, schéma directeur et analyse juridico-financière : les prérequis avant de déposer un dossier de financement.",
      },
    ],
    steps: [
      {
        title: 'Étude de faisabilité',
        description: "Identifiez le potentiel d'énergies renouvelables local, les bâtiments à raccorder et le tracé optimal. L'étude est en partie financée.",
      },
      {
        title: 'Schéma directeur et gouvernance',
        description: "Définissez la stratégie de développement et le mode de gestion adapté à votre territoire.",
      },
      {
        title: 'Montage financier et autorisations',
        description: "Cumul de l'aide nationale à la chaleur renouvelable, primes et prêts bonifiés.",
      },
      {
        title: 'Construction et raccordements',
        description: "Construction de la chaufferie, pose du réseau, raccordement progressif des bâtiments. Mise en service par tranches.",
      },
    ],
    faq: [
      {
        question: "Quel taux d'énergies renouvelables pour bénéficier des aides ?",
        answer:
          "L'aide nationale exige que la chaleur soit produite principalement à partir d'énergies renouvelables. La TVA à 5,5 % impose au moins 50 % d'énergies renouvelables dans le mix.",
      },
      {
        question: "Quel mode de gestion choisir ?",
        answer:
          "Régie directe pour les petits réseaux. Société d'économie mixte pour mutualiser tout en gardant le contrôle. Délégation de service public confiée à un opérateur privé.",
      },
      {
        question: "Combien de temps pour créer un réseau ?",
        answer:
          "Plusieurs années entre l'étude et la mise en service : étude (6-12 mois), schéma directeur (12-18 mois), procédure (12-18 mois), montage (12 mois), travaux (12-24 mois).",
      },
    ],
    ctaLabel: 'Étudier mon réseau de chaleur',
    ctaHref: '/contact?source=collectivites&besoin=reseau-chaleur',
    secondaryCta: { label: 'Aides chaleur renouvelable', href: '/collectivites/aides/fonds-chaleur' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'bailleurs-sociaux',
    iconName: 'building',
    eyebrow: 'Parc social',
    title: 'Bailleurs sociaux',
    shortLabel: 'Bailleurs sociaux',
    lead: "Des millions de logements sociaux restent à rénover. Les dispositifs mobilisables : primes collectives, prêts bonifiés et dotations d'investissement.",
    badge: 'Parc social',
    badgeLabel: 'OPH, ESH, SEM, coopératives HLM',
    keyPoints: [
      {
        iconName: 'building',
        label: "Rénovation thermique d'immeubles",
        description: "Isolation, remplacement des chaudières collectives, ventilation, réseaux d'eau chaude. Objectif : sortir les logements des étiquettes les plus basses.",
      },
      {
        iconName: 'sparkles',
        label: 'Primes collectives',
        description: "Plusieurs primes sont disponibles pour la rénovation des logements collectifs. Des bonus peuvent s'appliquer pour les rénovations performantes.",
      },
      {
        iconName: 'banknote',
        label: 'Prêts bonifiés',
        description: "Des prêts à taux avantageux sont proposés aux bailleurs sociaux pour financer la rénovation thermique.",
      },
      {
        iconName: 'users',
        label: 'Concertation avec les locataires',
        description: "La concertation préalable est obligatoire. Informez vos locataires au moins 6 mois à l'avance pour les opérations lourdes.",
      },
    ],
    steps: [
      {
        title: 'Audit et priorisation',
        description: "Audit énergétique du parc, identification des immeubles prioritaires (étiquettes les plus basses, occupation stable).",
      },
      {
        title: 'Programmation et concertation',
        description: "Planification pluriannuelle. Concertation obligatoire avec les instances de locataires.",
      },
      {
        title: 'Montage financier',
        description: "Cumul primes, prêts bonifiés et aides régionales. Marchés de travaux.",
      },
      {
        title: 'Travaux occupés ou à vide',
        description: "Travaux en site occupé pour l'isolation extérieure et les équipements communs. Interventions lourdes en site vide.",
      },
    ],
    faq: [
      {
        question: "Les aides nationales à la rénovation s'appliquent-elles au parc social ?",
        answer:
          "Les aides ciblent principalement les copropriétés privées. Pour le parc social, les dispositifs principaux sont les prêts bonifiés, les primes collectives et les dotations.",
      },
      {
        question: "Quelles primes pour le logement social ?",
        answer:
          "Plusieurs primes couvrent l'isolation, le chauffage performant, la ventilation et la rénovation globale. Des bonus s'appliquent pour les rénovations à fort gain énergétique.",
      },
      {
        question: "Comment gérer les travaux en site occupé ?",
        answer:
          "La majorité des rénovations se fait en site occupé. Anticipez les nuisances, informez les locataires, coordonnez avec le gestionnaire de proximité.",
      },
    ],
    ctaLabel: 'Diagnostic parc social',
    ctaHref: '/contact?source=collectivites&besoin=bailleurs-sociaux',
    secondaryCta: { label: 'Prêts bonifiés', href: '/collectivites/aides/banque-territoires' },
  },
];

export function getPatrimoineCollectiviteStub(slug: string): ParcoursStub | undefined {
  return PATRIMOINE_COLLECTIVITES.find((p) => p.slug === slug);
}
